import { defineStore } from "pinia";
import apiClient from "@/utils/axios";
import { useHttpErrorManager } from "@/composables/useHttpErrorManager";
import { useAppHelpers } from "@/composables/useAppHelpers";

export const useAuthStore = defineStore("authStore", {
    state() {
        return {
            userData: null,
            userDataFetched: false,
            isLogoutAllowed: false,
            isUpdatingCSRF: false,
        };
    },
    getters: {
        isLoggedIn() {
            return (
                this.userData !== undefined &&
                this.userData !== null &&
                Object.keys(this.userData).length > 0
            );
        },
        is_admin() {
            return this.isLoggedIn && this.getUserType === "admin";
            
        },
        is_active() {
            return this.isLoggedIn && this.getStatus === "active";
            
        },
        has_verified_email() {
            return this.isLoggedIn && this.getEmailStatus === "verified";
            
        },
        is_moderator() {
            return this.isLoggedIn && this.getUserType === "moderator";
            
        },
        is_member() {
            return this.isLoggedIn && this.getUserType === "member";
            
        },
        user() {
            return JSON.parse(this.userData);
        },
        getUserType() {
            if (this.isLoggedIn) {
                if (this.user.type !== undefined) {
                    return this.user.type;
                }
                return "member";
            }
            return "guest";
        },
        getEmailStatus() {
            if (this.isLoggedIn) {
                if (
                    this.user.email_verified_at !== undefined &&
                    this.user.email_verified_at !== null
                ) {
                    return "verified";
                }
                return "not_verified";
            }
            return "not_verified";
        },
        getStatus() {
            if (this.isLoggedIn) {
                if (this.user.status !== undefined) {
                    return this.user.status;
                }
                return "blocked";
            }
            return "blocked";
        },
    },
    actions: {
        performLogout() {
            this.isLogoutAllowed = true;
        },
        CSRF_UPDATING(val) {
            this.isUpdatingCSRF = val;
        },
        async FETCH_USER_DATA(force = false) {
            if(!this.userDataFetched || force){
                try {
                    const response = await apiClient.get("/auth/user", {});
                    this.userDataFetched = true;
                    let user = response.data.userData;
                    this.userData = JSON.stringify(user);
                } catch (error) {
                    await useHttpErrorManager().handleError(error,false);
                    this.userDataFetched = false;
                    if (this.isLoggedIn) {
                        await this.processLogout();
                    }
                    this.userData = null;                    
                }
            }
        },
        UPDATE_USER_DATA(val) {
            this.userData = val;
        },
        async enableTwoFactor () {
            try {
                return await apiClient.post("/auth/user/two-factor-authentication", {});
            } catch (error) {
                return await useHttpErrorManager().handleError(error, true);
            }
        },
        async disableTwoFactor () {
            try {
                return await apiClient.delete("/auth/user/two-factor-authentication", {});
            } catch (error) {
                return await useHttpErrorManager().handleError(error, true);
            }
        },
        async twoFactorQrCode () {
            try {
                const response = await apiClient.get("/auth/user/two-factor-qr-code");
                let dataToReturn = {};
                if (response.status === 200) {
                    dataToReturn['response'] = response;
                    dataToReturn['data'] = response.data;
                    dataToReturn['svg'] = response.data.svg;
                    dataToReturn['code'] = useAppHelpers().extractTwoFactorConnectionCode(response.data.url);
                }
                return dataToReturn;
            }
            catch (error) {
                return await useHttpErrorManager().handleError(error, true);
            }
        },
        async processLogout() {
            try{
                const response = await apiClient.post("/logout");
                if(response.status >= 200 && response.status < 300){
                    this.userData = null;
                    this.userDataFetched = false;
                    this.isLogoutAllowed = false;
                    this.$router.push({ name: 'home' });
                }
            }
            catch(error){
                await useHttpErrorManager().handleError(error,true);
                this.isLogoutAllowed = false;
                this.$router.push({ name: 'home' });
            }
            
        },
        async canAccessRoute(route) {
            let allowedTypes = route.meta?.type;
            let userType = this.getUserType;

            if((allowedTypes === undefined) || (userType && allowedTypes.includes(userType)) || (userType !== 'guest' && allowedTypes.includes('all_auth')) || (userType === 'guest' && allowedTypes.includes('auth_layout'))){
                return true;
            }
            return false;
        }
    },
});
